/**
 * Fleet-wide conversion capture, installed without asking the site.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `useConversionTracking` shipped in @duffcloudservices/cms, was exported from the package
 * index, was documented with an example, and was adopted by ZERO of the eleven fleet sites.
 * Two independent reviews on 2026-07-26 measured what that cost (C-326):
 *
 *   - Bryan's Handyman Solutions exists to generate phone calls. It ships five `tel:` CTAs
 *     — hero, header, mobile icon, sticky call bar, footer — and every one of them fires
 *     nothing. `trackEvent` appears in the bundle exactly once: as the composable's own
 *     unused export. Its GA4 property was never created either, so the site has no
 *     measurement of any kind for the only action it is built to produce.
 *   - Kim Duff Homes, a flagship account, has 6 pageViews in App Insights across 365 days.
 *
 * The lesson is not "write better docs". A capability the site must remember to switch on
 * is a capability that is off. So capture is a SIDE EFFECT of importing the package every
 * fleet site already depends on, and it finds its own transport.
 *
 * HOW A SITE'S EVENTS GET OUT — three sinks, in the order they are usually available
 * ---------------------------------------------------------------------------------
 *  1. GA4 (`window.gtag`). The deploy workflow injects the gtag snippet into every built
 *     HTML file when `.dcs/site.yaml` carries a `google_analytics_id`. That means a site
 *     with GA4 configured captures conversions with LITERALLY no code change — which is
 *     precisely why the empty-`google_analytics_id` launch gate is the other half of C-326.
 *  2. `window.__dcsConversionAttach(sink)` — a one-line, import-free contract published by
 *     this module for any telemetry transport that boots later. `@duffcloudservices/telemetry`
 *     calls it automatically inside `initialize()`; a site with a bespoke App Insights boot
 *     adds the single line. Events captured before that arrive are buffered, not lost.
 *  3. `attachConversionSink(sink)` — the direct API, for callers holding the import.
 *
 * WHERE IT DELIBERATELY DOES NOTHING
 * ----------------------------------
 *  - No `document` (SSR, VitePress build, the Playwright prerender pass).
 *  - Inside an iframe — the DCS visual editor and preview surfaces frame the site, and
 *     an editor clicking around a customer's page is not a lead.
 *  - Do Not Track is on.
 *  - The page opted out via `window.__dcsConversionOptOut = true` or
 *    `<html data-dcs-analytics="off">`.
 *
 * In every one of those cases it binds no listener and buffers nothing.
 */
import {
  attachConversionSink,
  createConversionTracker,
  doNotTrackEnabled,
  type ConversionEvent,
  type ConversionSink,
  type ConversionTracker,
  type ConversionTrackingOptions,
} from './composables/useConversionTracking'
import { syntheticCaptureDetected } from './syntheticCapture'

/** Globals this module reads or publishes. Declared once so the reads stay typed. */
type ConversionWindow = Window & {
  gtag?: (command: string, ...args: unknown[]) => void
  __dcsConversionOptOut?: boolean
  __dcsConversionAttach?: (sink: ConversionSink) => number
  __dcsConversionInstalled?: boolean
}

/** Options for {@link installConversionCapture}. */
export interface InstallConversionCaptureOptions
  extends Omit<ConversionTrackingOptions, 'sink' | 'mirrors' | 'force'> {
  /**
   * Emit through GA4's `window.gtag` when it is present. Default `true`.
   *
   * Looked up at emit time, not install time: the gtag snippet is `async`, so on a fast
   * connection the site's JS can install this before Google's script has defined `gtag`.
   */
  gtag?: boolean
}

/**
 * The module-level singleton.
 *
 * Idempotence matters more here than anywhere else in the package: this runs on import, and
 * a site whose bundler produces two copies of the module (a pnpm-hoisting accident, a
 * duplicated dependency) would otherwise install two listeners and double every conversion.
 * The `window.__dcsConversionInstalled` flag closes that across module instances; the local
 * `installed` closes it within one.
 */
let installed: ConversionTracker | null = null

function conversionWindow(): ConversionWindow | null {
  return typeof window !== 'undefined' ? (window as ConversionWindow) : null
}

/** Whether the page has explicitly asked not to be measured. */
export function conversionOptedOut(): boolean {
  const w = conversionWindow()
  if (w?.__dcsConversionOptOut === true) return true
  if (typeof document !== 'undefined') {
    const flag = document.documentElement?.getAttribute('data-dcs-analytics')
    if (flag === 'off' || flag === 'false') return true
  }
  return false
}

/**
 * Whether this document is being rendered inside a frame.
 *
 * The DCS portal embeds customer sites in an iframe for the visual editor and preview
 * (`dcsEditorPlugin` keys off the same `window.parent !== window` test). Counting an
 * editor's clicks as customer conversions would inflate exactly the number the owner
 * report is supposed to make trustworthy.
 */
export function inFrame(): boolean {
  const w = conversionWindow()
  if (!w) return false
  try {
    return w.parent !== w || w.top !== w.self
  } catch {
    // Cross-origin framing throws on access — which is itself proof we are framed.
    return true
  }
}

/** Reason the installer declined, or `null` when it installed. */
export type ConversionInstallSkipReason =
  | 'no-document'
  | 'already-installed'
  | 'in-frame'
  | 'do-not-track'
  | 'opted-out'
  | 'synthetic-capture'

/** Result of an {@link installConversionCapture} call. */
export interface ConversionInstallResult {
  installed: boolean
  reason: ConversionInstallSkipReason | null
  tracker: ConversionTracker | null
}

/**
 * Send an event to GA4 if the page has one.
 *
 * GA4 event params are a flat string map and must not carry PII — which the event's
 * properties already satisfy, because `redactHref` replaced any contact string with a
 * digest before the event was ever built.
 */
function gtagSink(event: ConversionEvent): void {
  const w = conversionWindow()
  if (typeof w?.gtag !== 'function') return
  w.gtag('event', event.name, { ...event.properties })
}

/**
 * Install fleet conversion capture. Idempotent; safe to call from anywhere, any number of
 * times, on the server or in a frame.
 *
 * Called for its side effect from the package index, so a site gets this by importing
 * `@duffcloudservices/cms` at all. Call it directly only to pass per-site configuration
 * (extra `bookingHosts`, a self-hosted `bookingPaths`) — do that BEFORE the first click,
 * i.e. at app entry, since the first call wins.
 */
export function installConversionCapture(
  options: InstallConversionCaptureOptions = {},
): ConversionInstallResult {
  const skip = (reason: ConversionInstallSkipReason): ConversionInstallResult => ({
    installed: false,
    reason,
    tracker: null,
  })

  if (typeof document === 'undefined') return skip('no-document')

  const w = conversionWindow()
  if (installed || w?.__dcsConversionInstalled === true) return skip('already-installed')
  if (inFrame()) return skip('in-frame')
  if ((options.respectDoNotTrack ?? true) && doNotTrackEnabled()) return skip('do-not-track')
  if (conversionOptedOut()) return skip('opted-out')
  // C-614: the post-deploy capture pass walks the LIVE site with real Chromium.
  // Its clicks are ours, not the owner's. Declining here is the conversion-side
  // half of the same suppression each site applies to its pageView boot.
  if (syntheticCaptureDetected()) return skip('synthetic-capture')

  const useGtag = options.gtag ?? true

  // GA4 goes in as a MIRROR, never as the sink. `gtagSink` re-reads `window.gtag` on every
  // event (so a snippet that lands after install still gets the hit), and because it is a
  // mirror it does not satisfy the tracker's "a sink exists" test — which keeps the
  // deferred-App-Insights buffer armed on GA4 sites instead of silently disabling it.
  const tracker = createConversionTracker({
    ...options,
    sink: undefined,
    mirrors: useGtag ? [gtagSink] : [],
  })
  tracker.start()

  installed = tracker
  if (w) {
    w.__dcsConversionInstalled = true
    // The import-free contract. The sink lands as the tracker's PRIMARY, which drains the
    // buffer; GA4 keeps receiving separately as a mirror. Two stores, one event in each —
    // not a double count within either.
    w.__dcsConversionAttach = (sink: ConversionSink): number => attachConversionSink(sink)
  }

  return { installed: true, reason: null, tracker }
}

/** The installed tracker, or `null`. Exposed for tests and diagnostics. */
export function installedConversionTracker(): ConversionTracker | null {
  return installed
}

/**
 * Tear the auto-installed tracker down and clear the global flags.
 *
 * Exists for tests and for a site that genuinely needs to re-install with different
 * options. Not part of the normal runtime path.
 */
export function resetConversionCapture(): void {
  installed?.stop()
  installed = null
  const w = conversionWindow()
  if (w) {
    delete w.__dcsConversionInstalled
    delete w.__dcsConversionAttach
  }
}
